// Core packages

// Third party packages

// Custom packages
import {
  ZipDownloadMessage,
  zipDownloadErrorMessage,
  zipFileNameFromContentDisposition,
} from "src/app/modules/properties/document-files-zip.utils";

/**
 * Pure helpers of the dossier zip on the 3.0.0 archive (NEW-M17): the dialog that lists the parts of
 * `GET /document-archive-docs/export/dossier` and downloads them, one at a time, from
 * `GET /document-archive-docs/export/dossier/:part`.
 *
 * The back-end composes the dossier, names the zips and tells how many files it left out. What is left
 * here is checking the list before offering it, printing each part, and picking a translated text for
 * each failure: the back-end messages are technical and in English. The texts the dossier shares with
 * «Scarica tutti» (session expired, too many requests, files left out, generic failure) are the same
 * keys, through `zipDownloadErrorMessage` and `skippedFilesMessage`.
 *
 * @since 3.0.0
 */

/** Prefix of the i18n keys of the dialog */
const I18N_PREFIX = "properties.documentsV3.dossierDialog";

/** Name of a saved part when neither the response nor the list names it: the back-end's own fallback */
const FALLBACK_PART_FILE_NAME = "fascicolo.zip";

const KIB = 1024;
const MIB = KIB * 1024;
const GIB = MIB * 1024;

/**
 * One part of the dossier, as the dialog lists it.
 *
 * @since 3.0.0
 */
export interface DossierPartRow {
  /** What the download asks for: "1"…"N", or "contracts" */
  part: string;
  /** The name of the zip, as the list gives it */
  fileName: string;
  /** Files in the zip; 0 when the list did not say */
  files: number;
  /** Bytes of those files; 0 when the list did not say, and then no weight is printed */
  bytes: number;
}

function isFilledString(value: unknown): value is string {
  return typeof value === "string" && value.trim() !== "";
}

function isCount(value: unknown): value is number {
  return typeof value === "number" && isFinite(value) && value >= 0 && Math.floor(value) === value;
}

/**
 * The parts of the list, in the order the back-end sends them (the numbered ones, then the
 * contracts), or null when the answer cannot be trusted whole: no list of parts, a part without the
 * names it is downloaded and saved by, or the same part twice. A list with a part left out would hide
 * its files, so it is a failure, never a shorter list.
 *
 * @param data `data` of `GET /document-archive-docs/export/dossier`
 */
export function dossierPartRows(data: unknown): DossierPartRow[] | null {
  if (!data || typeof data !== "object") {
    return null;
  }
  const parts = (data as { parts?: unknown }).parts;
  if (!Array.isArray(parts)) {
    return null;
  }

  const rows: DossierPartRow[] = [];
  const seen: { [part: string]: true } = {};
  for (const entry of parts) {
    if (!entry || typeof entry !== "object") {
      return null;
    }
    const item = entry as { part?: unknown; fileName?: unknown; files?: unknown; bytes?: unknown };
    const part = item.part;
    const fileName = item.fileName;
    if (!isFilledString(part) || !isFilledString(fileName) || seen[part]) {
      return null;
    }
    seen[part] = true;
    rows.push({
      part,
      fileName,
      files: isCount(item.files) ? item.files : 0,
      bytes: isCount(item.bytes) ? item.bytes : 0,
    });
  }
  return rows;
}

/**
 * Weight of a part, as printed next to its name: bytes under 1 KB, KB under 1 MB, MB under 1 GB, GB
 * above, with the decimal comma in Italian. Empty for 0 or an unusable value.
 *
 * @param bytes `bytes` of the part
 * @param lang  language of the session
 */
export function dossierPartSizeLabel(bytes: number, lang: string): string {
  if (!(typeof bytes === "number" && isFinite(bytes) && bytes > 0)) {
    return "";
  }
  if (bytes < KIB) {
    return `${Math.round(bytes)} B`;
  }
  let text: string;
  let unit: string;
  if (bytes < MIB) {
    text = (bytes / KIB).toFixed(1);
    unit = "KB";
  } else if (bytes < GIB) {
    text = (bytes / MIB).toFixed(1);
    unit = "MB";
  } else {
    text = (bytes / GIB).toFixed(2);
    unit = "GB";
  }
  return `${lang === "it" ? text.replace(".", ",") : text} ${unit}`;
}

/**
 * How many files a part holds, as printed next to its name.
 *
 * @param count `files` of the part
 */
export function dossierFilesCountMessage(count: number): ZipDownloadMessage {
  return count === 1
    ? { key: `${I18N_PREFIX}.filesOne` }
    : { key: `${I18N_PREFIX}.filesMany`, params: { count } };
}

/**
 * Text for a list of parts that could not be read, picked by HTTP status. 0 stands for a failure
 * without status (network down) and for an answer without its list. The dialog shows it in its body,
 * with «Riprova».
 *
 * @param status HTTP status of the failed response, 0 when there is none
 * @param retryAfterHeader `Retry-After` of a 429 response
 * @param nowMs current time, in milliseconds
 */
export function dossierListErrorMessage(
  status: number,
  retryAfterHeader: string | null | undefined,
  nowMs: number
): ZipDownloadMessage {
  switch (status) {
    case 401:
    case 429:
      return zipDownloadErrorMessage(status, retryAfterHeader, nowMs);
    case 403:
      return { key: `${I18N_PREFIX}.errors.forbidden` };
    case 404:
      return { key: `${I18N_PREFIX}.errors.notAvailable` };
    default:
      return { key: `${I18N_PREFIX}.errors.loadError` };
  }
}

/**
 * Text for a part that could not be downloaded, picked by HTTP status. 0 stands for a failure without
 * status: network down, or the connection cut once the zip had started.
 *
 * @param status HTTP status of the failed response, 0 when there is none
 * @param retryAfterHeader `Retry-After` of a 429 response
 * @param nowMs current time, in milliseconds
 */
export function dossierPartErrorMessage(
  status: number,
  retryAfterHeader: string | null | undefined,
  nowMs: number
): ZipDownloadMessage {
  switch (status) {
    case 403:
      return { key: `${I18N_PREFIX}.errors.forbidden` };
    case 404:
      return { key: `${I18N_PREFIX}.errors.partsChanged` };
    case 410:
      return { key: `${I18N_PREFIX}.errors.gone` };
    default:
      return zipDownloadErrorMessage(status, retryAfterHeader, nowMs);
  }
}

/**
 * True when a failed download calls for reading the list again: a 404, since the back-end composes the
 * dossier again on every request and the part asked for may be gone (or the files, or the archive).
 * The list read again shows what is left.
 *
 * @param status HTTP status of the failed download, 0 when there is none
 */
export function shouldReloadDossierParts(status: number): boolean {
  return status === 404;
}

/**
 * Drops control characters and turns path separators into "_", the rule `document-files-zip.utils`
 * applies to the name of `Content-Disposition`: the name only names the saved file and must not be able
 * to address a folder.
 */
function safeFileName(name: string): string | null {
  const cleaned = name
    .split("")
    .filter((char) => {
      const code = char.charCodeAt(0);
      return code > 0x1f && code !== 0x7f;
    })
    .join("")
    .replace(/[\\/]/g, "_")
    .trim();
  return cleaned !== "" ? cleaned : null;
}

/**
 * Name of a saved part: the one the back-end sends in `Content-Disposition`, else the one of the list,
 * else "fascicolo.zip".
 *
 * @param contentDisposition value of the `Content-Disposition` response header
 * @param listFileName `fileName` of the part in the list
 */
export function dossierPartSaveName(
  contentDisposition: string | null | undefined,
  listFileName: unknown
): string {
  const fromHeader = zipFileNameFromContentDisposition(contentDisposition);
  if (fromHeader !== null) {
    return fromHeader;
  }
  const fromList = typeof listFileName === "string" ? safeFileName(listFileName) : null;
  return fromList !== null ? fromList : FALLBACK_PART_FILE_NAME;
}
